import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './CategoriesTab.css';
import useSuperAdminCatalogManagementStore from '@/stores/SuperAdminStore/CatalogManagementStore';
import { Category } from '@/services/PagesServices/SuperAdminCatalogService';
import LoadingSpinner from '@/Components/common/LoadingSpinner';
import ErrorDisplay from '@/Components/common/ErrorDisplay';

interface ModalState {
  isOpen: boolean;
  type: 'root' | 'subcategory' | 'edit' | 'editSubcategory';
  parentCategory?: Category;
  editingCategory?: Category;
}

const CategoriesTab: React.FC = () => {
  const {
    categories,
    loading,
    error,
    fetchCategories,
    addCategory,
    addSubcategory,
    editCategory,
    editSubcategory,
    removeCategory,
    removeSubcategory,
    toggleCategoryExpand
  } = useSuperAdminCatalogManagementStore();

  const [modalState, setModalState] = useState<ModalState>({
    isOpen: false,
    type: 'root',
  });

  const [formData, setFormData] = useState({
    name: '',
  });

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const openAddRootModal = () => {
    setModalState({ isOpen: true, type: 'root' });
    setFormData({ name: '' });
  };

  const openAddSubcategoryModal = (parentCategory: Category) => {
    setModalState({ isOpen: true, type: 'subcategory', parentCategory });
    setFormData({ name: '' });
  };

  const openEditModal = (category: Category) => {
    setModalState({ isOpen: true, type: 'edit', editingCategory: category });
    setFormData({ name: category.name });
  };

  const openEditSubcategoryModal = (subcategory: Category, parentCategory: Category) => {
    setModalState({ isOpen: true, type: 'editSubcategory', editingCategory: subcategory, parentCategory });
    setFormData({ name: subcategory.name });
  };

  const closeModal = () => {
    setModalState({ isOpen: false, type: 'root' });
    setFormData({ name: '' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (modalState.type === 'root') {
        await addCategory(formData.name);
      } else if (modalState.type === 'subcategory' && modalState.parentCategory) {
        await addSubcategory(modalState.parentCategory.id, formData.name);
      } else if (modalState.type === 'edit' && modalState.editingCategory) {
        await editCategory(modalState.editingCategory.id, formData.name);
      } else if (modalState.type === 'editSubcategory' && modalState.editingCategory) {
        await editSubcategory(modalState.editingCategory.id, formData.name);
      }
      closeModal();
    } catch (error: any) {
      alert(error.message || 'An error occurred');
    }
  };

  const handleDelete = async (categoryId: number, isSubcategory: boolean = false) => {
    if (!window.confirm(`Are you sure you want to delete this ${isSubcategory ? 'subcategory' : 'category'}?`)) {
      return;
    }

    try {
      if (isSubcategory) {
        await removeSubcategory(categoryId);
      } else {
        await removeCategory(categoryId);
      }
    } catch (error: any) {
      alert(error.message || 'An error occurred');
    }
  };

  const toggleExpand = (categoryId: number) => {
    toggleCategoryExpand(categoryId);
  };

  const renderCategoryItem = (category: Category, isSubcategory: boolean = false, parentCategory?: Category) => (
    <div
      key={category.id}
      className={`admin-catalog-category-item ${isSubcategory ? 'admin-catalog-subcategory' : ''}`}
    >
      <div className="admin-catalog-category-content">
        {!isSubcategory && category.subcategories && (
          <button
            className="admin-catalog-expand-button"
            onClick={() => toggleExpand(category.id)}
            aria-label={category.isExpanded ? 'Collapse' : 'Expand'}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              className={`admin-catalog-expand-icon ${category.isExpanded ? 'admin-catalog-expanded' : ''}`}
            >
              <path
                d="M9 18l6-6-6-6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}

        <div className="admin-catalog-category-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M3 7a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M3 7l9 6 9-6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <div className="admin-catalog-category-info">
          <div className="admin-catalog-category-header">
            <h3 className="admin-catalog-category-name">{category.name}</h3>
            <span className="admin-catalog-course-count">{category.courseCount} courses</span>
          </div>
        </div>

        <div className="catalog-actions-wrapper">
          {!isSubcategory && (
            <button
              className="catalog-action-btn catalog-add-sub-btn"
              aria-label="Add subcategory"
              onClick={(e) => {
                e.stopPropagation();
                openAddSubcategoryModal(category);
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 5v14M5 12h14"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          )}
          <button
            className="catalog-action-btn catalog-edit-btn"
            aria-label="Edit category"
            onClick={(e) => {
              e.stopPropagation();
              if (isSubcategory && parentCategory) {
                openEditSubcategoryModal(category, parentCategory);
              } else {
                openEditModal(category);
              }
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <button
            className="catalog-action-btn catalog-delete-btn"
            aria-label="Delete category"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(category.id, isSubcategory);
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>

      {category.isExpanded && category.subcategories && (
        <div className="admin-catalog-subcategories">
          {category.subcategories.map(subcat => renderCategoryItem(subcat, true, category))}
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className="admin-catalog-section-header">
        <h2 className="admin-catalog-section-title">Category Hierarchy</h2>
        <button className="admin-catalog-add-root-button" onClick={openAddRootModal}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 5v14M5 12h14"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          Add Root Category
        </button>
      </div>

      {loading && !categories && <LoadingSpinner message="Loading categories..." />}

      {error && (
        <ErrorDisplay
          message={error}
          type="error"
          onRetry={fetchCategories}
        />
      )}

      {!loading && !error && categories && categories.length === 0 && (
        <div className="admin-catalog-empty-state">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M3 7a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M3 7l9 6 9-6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <h3>No Categories Yet</h3>
          <p>Start by creating your first category to organize your courses.</p>
          <button className="admin-catalog-add-root-button" onClick={openAddRootModal}>
            Add Your First Category
          </button>
        </div>
      )}

      {!loading && !error && categories && categories.length > 0 && (
        <div className="admin-catalog-categories-list">
          {categories.map(category => renderCategoryItem(category))}
        </div>
      )}

      {/* Modal for Add Category/Subcategory */}
      {modalState.isOpen && ReactDOM.createPortal(
        <div className="admin-catalog-modal-overlay" onClick={closeModal}>
          <div className="admin-catalog-modal-content" onClick={(e) => e.stopPropagation()}>
            {modalState.type === 'subcategory' && modalState.parentCategory && (
              <div className="admin-catalog-modal-parent-info">
                <h3>Parent Category</h3>
                <div className="admin-catalog-parent-category-badge">
                  <div className="admin-catalog-category-icon admin-catalog-small">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M3 7a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M3 7l9 6 9-6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div className="admin-catalog-parent-info">
                    <div className="admin-catalog-parent-info-header">
                      <span className="admin-catalog-parent-name">{modalState.parentCategory.name}</span>
                      <span className="admin-catalog-parent-course-count">{modalState.parentCategory.courseCount} courses</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <h2 className="admin-catalog-modal-title">
              {modalState.type === 'root' && 'Add Category'}
              {modalState.type === 'subcategory' && 'Add Subcategory'}
              {modalState.type === 'edit' && 'Edit Category'}
              {modalState.type === 'editSubcategory' && 'Edit Subcategory'}
            </h2>

            <form onSubmit={handleSubmit}>
              <div className="admin-catalog-form-group">
                <label htmlFor="categoryName" className="admin-catalog-form-label">
                  Category Name <span className="admin-catalog-required">*</span>
                </label>
                <input
                  type="text"
                  id="categoryName"
                  className="admin-catalog-form-input"
                  placeholder="e.g., Technical Skills"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div className="admin-catalog-modal-actions">
                <button type="submit" className="admin-catalog-btn-save" disabled={loading}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M17 21v-8H7v8M7 3v5h8"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {loading ? 'Saving...' : (
                    modalState.type === 'root' ? 'Save Category' :
                    modalState.type === 'subcategory' ? 'Save Sub-Category' :
                    modalState.type === 'edit' ? 'Update Category' :
                    'Update Sub-Category'
                  )}
                </button>
                <button type="button" className="admin-catalog-btn-cancel" onClick={closeModal} disabled={loading}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default CategoriesTab;
